import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export type ButtonProps = {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  /** Shows spinner, disables control — form submit / async actions. */
  loading?: boolean;
  className?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>;

const variantClass: Record<ButtonVariant, string> = {
  primary:
    'bg-ds-brand text-ds-inverse hover:bg-ds-brand-hover border border-transparent disabled:bg-ds-muted-bg disabled:text-ds-disabled',
  secondary:
    'bg-ds-surface text-ds-text border border-ds-border hover:bg-ds-subtle hover:border-ds-border-strong disabled:text-ds-disabled',
  ghost:
    'bg-transparent text-ds-text-secondary border border-transparent hover:bg-ds-subtle hover:text-ds-text disabled:text-ds-disabled',
  danger:
    'bg-ds-danger text-ds-inverse hover:bg-ds-danger-hover border border-transparent disabled:bg-ds-muted-bg disabled:text-ds-disabled',
};

const sizeClass: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-ds-sm gap-1',
  md: 'h-8 px-3 text-ds-sm gap-1.5',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    variant = 'secondary',
    size = 'md',
    leftIcon,
    rightIcon,
    loading = false,
    className,
    type = 'button',
    disabled,
    ...props
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-ds-md font-medium transition-colors duration-ds-fast ease-ds',
        'disabled:pointer-events-none disabled:opacity-60',
        variantClass[variant],
        sizeClass[size],
        className,
      )}
      {...props}
    >
      {loading ? (
        <Spinner
          size="sm"
          className={cn(
            variant === 'primary' || variant === 'danger'
              ? 'border-ds-inverse/30 border-t-ds-inverse'
              : undefined,
          )}
          label="Loading"
        />
      ) : leftIcon ? (
        <span className="inline-flex shrink-0 [&_svg]:size-4">{leftIcon}</span>
      ) : null}
      {children}
      {!loading && rightIcon ? (
        <span className="inline-flex shrink-0 [&_svg]:size-4">{rightIcon}</span>
      ) : null}
    </button>
  );
});
